/**
 * DPIPersistenceService - DPI detection, caching, and device scaling
 * 
 * This service ensures that DPI changes are detected, cached, and applied
 * consistently across browser refreshes and monitor changes.
 * 
 * NEW ARCHITECTURE: Supports WORLD_PX vs DEVICE_PX separation
 * - Layout always happens in WORLD_PX (96dpi logical space)
 * - Rendering applies device scaling via getDeviceScale()
 * 
 * CRITICAL: DPI is RENDER-TIME ONLY
 * - Do NOT send DPI to backend typesetter/layout services
 * - DPI only affects frontend rendering and canvas scaling
 * - Backend layout always uses WORLD_PX (96dpi logical space)
 * - Only include DPI for raster exports/thumbnails
 * 
 * CANVAS SCALING: Choose ONE approach
 * - Option A: Backing-store scaling (canvas.width/height *= DPR)
 * - Option B: CSS transform scaling (transform: scale())
 * - Do NOT use both - causes blur or double scaling
 */

const DPI_STORAGE_KEY = 'edgedocs-wordprocessing-dpi';
const DEFAULT_DPI = 96;
const DPI_TTL_HOURS = 24; // DPI cache expires after 24 hours

export class DPIPersistenceService {
  private static listeners: Set<() => void> = new Set();
  private static cachedDPI: number | null = null;
  private static detectionInProgress: boolean = false;
  private static lastDevicePixelRatio: number = 1;
  private static eventListenersAttached: boolean = false;
  private static cachedLoadDPI: number | null = null;
  private static debounceTimer: NodeJS.Timeout | null = null;
  private static lastLoggedScale: number | null = null;
  private static userZoom: number = 1.0; // User-set zoom [0.25..4.0], default 1.0

  /**
   * Detect display DPI using devicePixelRatio (DOM-safe, no font dependencies)
   * Returns devicePixelRatio * 96 for actual display density
   */
  static detectDisplayDPI(): number {
    try {
      const devicePixelRatio = window.devicePixelRatio || 1;
      const dpi = Math.round(96 * devicePixelRatio);

      console.log(`🔍 [DPI_DETECTION] Device pixel ratio: ${devicePixelRatio}, DPI: ${dpi}`);

      // Validate range and return
      if (dpi >= 72 && dpi <= 600) {
        return dpi;
      } else {
        console.warn(`⚠️ [DPI_DETECTION] DPI out of range: ${dpi}, using default: ${DEFAULT_DPI}`);
        return DEFAULT_DPI;
      }
    } catch (error) {
      console.warn('⚠️ [DPI_DETECTION] Error detecting DPI:', error);
      return DEFAULT_DPI;
    }
  }

  /**
   * Get DPI with auto-detection if not saved
   * FIXED: Use presence in storage, not value comparison
   */
  static getDPI(): number {
    // Return cached DPI if available
    if (this.cachedDPI !== null) {
      return this.cachedDPI;
    }

    const savedDPI = this.loadDPI();
    const hasSavedDPI = this.hasSavedDPI();
    
    // If we have a saved DPI (even if it's 96), cache and return it
    if (hasSavedDPI) {
      this.cachedDPI = savedDPI;
      return savedDPI;
    }

    // If no DPI is saved and we're not already detecting, start detection
    if (!this.detectionInProgress) {
      this.detectionInProgress = true;
      
      // Defer DPI detection until DOM is ready
      setTimeout(() => {
        try {
          const detectedDPI = this.detectDisplayDPI();
          // Always save detected DPI (even if it's 96)
          this.saveDPI(detectedDPI);
          this.cachedDPI = detectedDPI;
          console.log(`💾 [DPI_AUTO_DETECT] Auto-detected and saved DPI: ${detectedDPI}`);
        } catch (error) {
          console.warn('⚠️ [DPI_DETECTION] Error during auto-detection:', error);
          this.cachedDPI = DEFAULT_DPI;
        } finally {
          this.detectionInProgress = false;
        }
      }, 100); // Small delay to ensure DOM is ready
    }
    
    // Return default DPI while detection is in progress
    return DEFAULT_DPI;
  }

  /**
   * NEW ARCHITECTURE: Get device scale for rendering
   * Returns (dpi/96) * userZoom for WORLD_PX → DEVICE_PX conversion
   * Layout always happens in WORLD_PX, scaling only applied at render time
   * FIXED: Log only when scale changes by ≥1%
   */
  static getDeviceScale(): number {
    const dpi = this.getDPI();
    const scale = (dpi / 96) * this.userZoom;

    // Log when scale changes by ≥1% or on first call
    if (this.lastLoggedScale === null || Math.abs(scale - this.lastLoggedScale) >= 0.01) {
      console.log(`🔧 [DEVICE_SCALE] DPI=${dpi}, userZoom=${this.userZoom}, scale=${scale.toFixed(3)}`);
      this.lastLoggedScale = scale;
    }
    return scale;
  }

  /**
   * Set user zoom level [0.25..4.0]
   * This is separate from browser/OS zoom (DPR) and only affects rendering
   */
  static setUserZoom(zoom: number): void {
    const clampedZoom = Math.max(0.25, Math.min(4.0, zoom));
    if (Math.abs(clampedZoom - this.userZoom) > 0.01) {
      console.log(`🔍 [USER_ZOOM] Changed: ${this.userZoom} → ${clampedZoom}`);
      this.userZoom = clampedZoom;
      this.notifyListeners();
    }
  }

  /**
   * Get current user zoom level
   */
  static getUserZoom(): number {
    return this.userZoom;
  }

  /**
   * HARD GUARDRAIL: Runtime assertion to prevent DPI being passed to WASM
   * Throws error if any code attempts to send DPI/zoom to the typesetter worker
   * This ensures clean separation: WASM = layout in 96dpi world space, Frontend = device scaling
   */
  static assertNoDPIInfluence(params: any): void {
    if (params && typeof params === 'object') {
      const forbiddenKeys = ['dpi', 'DPI', 'zoom', 'Zoom', 'scale', 'Scale', 'devicePixelRatio', 'deviceScale'];
      for (const key of forbiddenKeys) {
        if (key in params) {
          throw new Error(
            `🚫 DPI GUARDRAIL VIOLATION: Attempted to pass '${key}' to WASM worker. ` +
            `DPI/zoom must NEVER influence layout. Use DPIPersistenceService.getDeviceScale() ` +
            `for render-time scaling only. Layout always uses 96dpi WORLD_PX coordinates.`
          );
        }
      }
    }
  }

  /**
   * Save DPI setting to localStorage with TTL
   * FIXED: Updates cache to prevent repeated detection
   */
  static saveDPI(dpi: number): void {
    try {
      if (dpi && dpi >= 72 && dpi <= 600) {
        const dpiData = {
          value: dpi,
          measuredAt: Date.now(),
          ttlHours: DPI_TTL_HOURS
        };
        localStorage.setItem(DPI_STORAGE_KEY, JSON.stringify(dpiData));
        this.cachedDPI = dpi; // Update cache
        console.log(`💾 [DPI_PERSISTENCE] Saved DPI: ${dpi} (TTL: ${DPI_TTL_HOURS}h)`);
        this.notifyListeners();
      } else {
        console.warn(`⚠️ [DPI_PERSISTENCE] Invalid DPI value: ${dpi}, not saving`);
      }
    } catch (error) {
      console.warn(`⚠️ [DPI_PERSISTENCE] Failed to save DPI:`, error);
    }
  }

  /**
   * Load DPI setting from localStorage with TTL validation
   * FIXED: Added caching to prevent repeated localStorage access and logging
   */
  static loadDPI(): number {
    // Return cached load result if available (no logging for cache hits)
    if (this.cachedLoadDPI !== null) {
      return this.cachedLoadDPI;
    }

    try {
      const savedData = localStorage.getItem(DPI_STORAGE_KEY);
      if (savedData) {
        // Try to parse as new TTL format first
        try {
          const dpiData = JSON.parse(savedData);
          if (dpiData.value && dpiData.measuredAt && dpiData.ttlHours) {
            const ageHours = (Date.now() - dpiData.measuredAt) / (1000 * 60 * 60);
            if (ageHours < dpiData.ttlHours) {
              const dpi = dpiData.value;
              if (dpi >= 72 && dpi <= 600) {
                this.cachedLoadDPI = dpi;
                // Only log when actually loading fresh data (not cached)
                console.log(`📂 [DPI_PERSISTENCE] Loaded fresh DPI: ${dpi} (age: ${ageHours.toFixed(1)}h)`);
                return dpi;
              }
            } else {
              console.log(`📂 [DPI_PERSISTENCE] DPI expired (age: ${ageHours.toFixed(1)}h), re-detecting`);
              localStorage.removeItem(DPI_STORAGE_KEY); // Clear expired data
            }
          }
        } catch (jsonError) {
          // Fallback to legacy format (plain number)
          const dpi = parseInt(savedData, 10);
          if (dpi >= 72 && dpi <= 600) {
            this.cachedLoadDPI = dpi;
            // Only log when actually loading fresh data (not cached)
            console.log(`📂 [DPI_PERSISTENCE] Loaded fresh legacy DPI: ${dpi}`);
            return dpi;
          } else {
            console.warn(`⚠️ [DPI_PERSISTENCE] Invalid saved DPI: ${dpi}, using default`);
          }
        }
      }
    } catch (error) {
      console.warn(`⚠️ [DPI_PERSISTENCE] Failed to load DPI:`, error);
    }
    
    this.cachedLoadDPI = DEFAULT_DPI;
    return DEFAULT_DPI;
  }

  /**
   * Clear saved DPI (for testing or reset)
   * FIXED: Clears all caches to force re-detection
   */
  static clearDPI(): void {
    try {
      localStorage.removeItem(DPI_STORAGE_KEY);
      this.cachedDPI = null; // Clear main cache
      this.cachedLoadDPI = null; // Clear load cache
      this.detectionInProgress = false; // Reset detection flag
      console.log(`🗑️ [DPI_PERSISTENCE] Cleared saved DPI and all caches`);
      this.notifyListeners();
    } catch (error) {
      console.warn(`⚠️ [DPI_PERSISTENCE] Failed to clear DPI:`, error);
    }
  }

  /**
   * Check if DPI is currently saved
   */
  static hasSavedDPI(): boolean {
    try {
      return localStorage.getItem(DPI_STORAGE_KEY) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Subscribe to DPI changes for cross-tab sync
   */
  static subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Enable cross-tab DPI synchronization and event-driven re-detection
   * FIXED: Add real-world event handling for monitor changes
   */
  static enableCrossTabSync(): void {
    if (typeof window === 'undefined') return;
    
    // Cross-tab sync
    window.addEventListener('storage', (e) => {
      if (e.key === DPI_STORAGE_KEY) {
        console.log('🔄 [DPI_SYNC] Cross-tab DPI change detected');
        // Clear all caches to force fresh detection
        this.cachedDPI = null;
        this.cachedLoadDPI = null;
        this.notifyListeners();
      }
    });

    // Event-driven re-detection for monitor changes
    if (!this.eventListenersAttached) {
      this.eventListenersAttached = true;
      this.lastDevicePixelRatio = window.devicePixelRatio || 1;

      // Monitor changes (resize, devicePixelRatio changes)
      window.addEventListener('resize', this.debounceReDetection.bind(this));
      
      // Page visibility changes (tab switching, monitor changes)
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.debounceReDetection();
        }
      });

      // Page show (back button, monitor changes)
      window.addEventListener('pageshow', this.debounceReDetection.bind(this));

      // Orientation changes (mobile devices)
      window.addEventListener('orientationchange', this.debounceReDetection.bind(this));

      // Poll devicePixelRatio for delayed updates (Chromium sometimes updates DPR slightly later)
      this.startDPRPolling();
    }
  }

  /**
   * Debounced re-detection to prevent excessive calls
   * FIXED: Actually forces re-detection and saves new DPI
   */
  private static debounceReDetection(): void {
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Debounce rapid events (250ms)
    this.debounceTimer = setTimeout(() => {
      const currentDPR = window.devicePixelRatio || 1;
      if (currentDPR !== this.lastDevicePixelRatio) {
        console.log(`🔄 [DPI_RECOMPUTE] Device pixel ratio changed: ${this.lastDevicePixelRatio} → ${currentDPR}`);
        this.lastDevicePixelRatio = currentDPR;
        
        // Force fresh detection and save new DPI
        this.forceReDetection();
      }
    }, 250);
  }

  /**
   * Force fresh DPI detection and save result
   */
  private static forceReDetection(): void {
    try {
      const detectedDPI = this.detectDisplayDPI();
      this.saveDPI(detectedDPI);
      this.cachedDPI = detectedDPI;
      this.cachedLoadDPI = detectedDPI;
      console.log(`🔄 [DPI_FORCE_RECOMPUTE] Re-detected and saved DPI: ${detectedDPI}`);
      this.notifyListeners();
    } catch (error) {
      console.warn('⚠️ [DPI_FORCE_RECOMPUTE] Error during forced re-detection:', error);
    }
  }

  /**
   * Start polling devicePixelRatio for delayed updates
   * Chromium sometimes updates DPR slightly later than resize events
   */
  private static startDPRPolling(): void {
    let pollCount = 0;
    const maxPolls = 10; // Poll for ~1 second (10 * 100ms)
    
    const pollDPR = () => {
      const currentDPR = window.devicePixelRatio || 1;
      if (currentDPR !== this.lastDevicePixelRatio) {
        console.log(`🔄 [DPI_POLL] Delayed DPR change detected: ${this.lastDevicePixelRatio} → ${currentDPR}`);
        this.lastDevicePixelRatio = currentDPR;
        this.forceReDetection();
        return; // Stop polling after change detected
      }
      
      pollCount++;
      if (pollCount < maxPolls) {
        setTimeout(pollDPR, 100); // Poll every 100ms
      }
    };
    
    // Start polling after a short delay
    setTimeout(pollDPR, 100);
  }

  /**
   * Notify all listeners of DPI changes
   */
  private static notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.warn('⚠️ [DPI_PERSISTENCE] Listener error:', error);
      }
    });
  }
}
